// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.
import "../../../style/sassStyle";

import { DummyEvent, QuestionnaireInputProps } from "./QuestionnaireTypes";
import { InputAdornment, TextField } from "@material-ui/core";
import React, { useCallback } from "react";

import Autocomplete from "@material-ui/lab/Autocomplete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/fontawesome-svg-core";

/**
 * Renders a simple text input in a questionaire.
 * You should not need to use this outside of the Questionnaire component.
 */
const QuestionnaireInputText = (props: QuestionnaireInputProps) => {
  const { question, onChange } = props;
  if (
    question.type !== "text" &&
    question.type !== "password" &&
    question.type !== "email" &&
    question.type !== "tel"
  ) {
    throw Error(
      `Invalid question type ${question.type} for QuestionnaireInputText.`
    );
  }

  const className = question.classNames?.textField;

  const typeahead =
    question.typeahead && question.typeahead.values
      ? question.typeahead.values
      : [];

  const onChangeWrapper = useCallback(
    (event: React.ChangeEvent<{}>, value: any) => {
      const dummyEvent: DummyEvent = {
        currentTarget: {
          name: question.name,
          value: value || "",
        },
      };
      onChange(dummyEvent);
    },
    [onChange, question.name]
  );

  const RenderInput = useCallback(
    params => {
      // This is needed to apply the formatter function
      params.inputProps.value = question.value || "";
      let label = question.label;
      if (label && question.mandatory) {
        label += " *";
      }

      return (
        <TextField
          {...params}
          error={!!question.error}
          value={question.value || ""}
          label={label}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {question.icon && (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={question.icon as IconName} />
                  </InputAdornment>
                )}
              </>
            ),
            endAdornment: question.endAdornment && (
              <InputAdornment position="end">
                {question.endAdornment}
              </InputAdornment>
            ),
          }}
          id={params.id}
          onChange={onChange}
          placeholder={question.placeholder}
          autoComplete={question.autofill ?? "no-fill"}
          type={question.type}
          color="primary"
          helperText={(question.error || "") + " "}
          className={className}
        />
      );
    },
    [
      className,
      onChange,
      question.autofill,
      question.endAdornment,
      question.error,
      question.icon,
      question.label,
      question.mandatory,
      question.placeholder,
      question.type,
      question.value,
    ]
  );

  return (
    <Autocomplete
      freeSolo
      options={typeahead}
      getOptionLabel={option => option}
      filterOptions={options => options}
      onChange={onChangeWrapper}
      value={question.value || ""}
      renderInput={RenderInput}
    />
  );
};

export default QuestionnaireInputText;
